import type { CalendarPlatform } from "@/types/contentCalendar.type";
import { distributionPublications } from "@/types/scheduling.type";
import type { UpcomingPost, UpcomingResult } from "../api/useScheduling";
import { dayLabel, liveFromDistribution } from "../live/liveModel";
import type { LiveRouteState } from "../live/liveTypes";

/** The row meta's words for a post's media (Pen: "Video 9:16", "Picture 4:5"). */
const MEDIA_WORD: Record<string, string> = { VIDEO: "Video", STATIC: "Picture", CAROUSEL: "Carousel" };

/** How a channel goes out, in the Schedule view's words (Pen: Live / Schedule row). */
export type ScheduleState = LiveRouteState | "live";

/** One channel of one post going live in the window. */
export interface ScheduleRow {
  key: string;
  post: UpcomingPost;
  platform: CalendarPlatform;
  state: ScheduleState;
  /** When it goes out (or the post's day when no time is set yet). */
  at: string;
  /** True when only the day is known. */
  dayOnly: boolean;
  title: string;
  /** "Video 9:16 · Heritage roots". */
  meta: string;
  thumbUrl: string | null;
  /** "by Rana", or "Kit sent" on a manual channel. */
  who: string | null;
  publicationId: string | null;
  /** The publication's status, so an optimistic schedule can be undone. */
  publicationStatus: string | null;
  canSchedule: boolean;
  error: string | null;
}

/** Needs you: nothing will post it yet (no account, not scheduled, or it failed). */
export function needsYou(row: Pick<ScheduleRow, "state">): boolean {
  return row.state === "connect" || row.state === "not-scheduled" || row.state === "failed";
}

export function isScheduled(row: Pick<ScheduleRow, "state">): boolean {
  return row.state === "scheduled" || row.state === "posting";
}

/** Every channel of every post, in the order they go live, inside the window. */
export function scheduleRows(data: UpcomingResult | undefined): ScheduleRow[] {
  if (!data) return [];
  const from = Date.parse(data.from);
  const to = Date.parse(data.to);
  const rows: ScheduleRow[] = [];
  for (const post of data.posts) {
    const { slot } = post;
    const live = liveFromDistribution({
      slotId: slot.id,
      mediaType: slot.mediaType,
      ratio: slot.aspectRatio,
      lengthSeconds: slot.lengthSeconds,
      channels: post.distribution.channels as CalendarPlatform[],
      distribution: post.distribution,
    });
    const publications = distributionPublications(post.distribution);
    const label = MEDIA_WORD[slot.mediaType] ?? "Post";
    const meta = [`${label} ${slot.aspectRatio}`, slot.pillarName || null].filter(Boolean).join(" · ");
    for (const entry of live.posts) {
      const channel = live.channels.find((c) => c.id === entry.channelId);
      if (!channel) continue;
      const publication = publications.find((p) => p.platform === channel.platform);
      const route = entry.route ?? null;
      const state: ScheduleState | null = entry.liveSince ? "live" : (route?.state ?? null);
      if (!state) continue;
      const at = entry.liveSince ?? route?.at ?? route?.day ?? slot.scheduledAt;
      const when = Date.parse(at);
      if (Number.isNaN(when) || when < from || when >= to) continue;
      const file = live.files.find((f) => f.id === entry.fileId) ?? live.files[0] ?? null;
      rows.push({
        key: `${slot.id}:${channel.id}`,
        post,
        platform: channel.platform,
        state,
        at,
        dayOnly: !entry.liveSince && !route?.at,
        title: slot.title || slot.ideaTitle || "Untitled post",
        meta,
        thumbUrl: file?.thumbUrl ?? null,
        who:
          state === "manual"
            ? live.kit?.sentAt
              ? "Kit sent"
              : null
            : publication?.scheduledByUserName && (state === "scheduled" || state === "posting" || state === "connect")
              ? `by ${publication.scheduledByUserName}`
              : null,
        publicationId: publication?.id ?? null,
        publicationStatus: publication?.status ?? null,
        canSchedule: !!route?.canSchedule,
        error: route?.error ?? null,
      });
    }
  }
  // A channel with only a day goes after that day's timed ones.
  const sortKey = (row: ScheduleRow) => (row.dayOnly ? `${row.at.slice(0, 10)}T23:59:59.999Z` : row.at);
  return rows.sort((a, b) => sortKey(a).localeCompare(sortKey(b)) || a.title.localeCompare(b.title));
}

/** Rows grouped by day in the brand's time zone: "Today · Wed 2 Dec", "Thu 10 Dec". */
export function scheduleDays(rows: ScheduleRow[], timeZone: string, now: number) {
  const today = dayLabel(new Date(now).toISOString(), timeZone);
  const tomorrow = dayLabel(new Date(now + 86_400_000).toISOString(), timeZone);
  const groups: { key: string; label: string; rows: ScheduleRow[] }[] = [];
  for (const row of rows) {
    const day = dayLabel(row.at, timeZone);
    const label = day === today ? `Today · ${day}` : day === tomorrow ? `Tomorrow · ${day}` : day;
    const last = groups.at(-1);
    if (last && last.key === day) last.rows.push(row);
    else groups.push({ key: day, label, rows: [row] });
  }
  return groups;
}

/** "6 channels go live in the next 14 days. 2 are scheduled, 1 is manual, 3 need you." */
export function scheduleSentence(rows: ScheduleRow[], days: number): string {
  if (rows.length === 0) return `Nothing goes live in the next ${days} days.`;
  const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const scheduled = rows.filter(isScheduled).length;
  const manual = rows.filter((row) => row.state === "manual").length;
  const needs = rows.filter(needsYou).length;
  const live = rows.filter((row) => row.state === "live").length;
  const parts = [
    scheduled ? `${scheduled} ${scheduled === 1 ? "is" : "are"} scheduled` : null,
    manual ? `${manual} ${manual === 1 ? "is" : "are"} manual` : null,
    live ? `${live} ${live === 1 ? "is" : "are"} live` : null,
    needs ? `${needs} need${needs === 1 ? "s" : ""} you` : null,
  ].filter(Boolean);
  return `${count(rows.length, "channel goes", "channels go")} live in the next ${days} days.${parts.length ? ` ${parts.join(", ").replace(/, ([^,]*)$/, ", $1")}.` : ""}`;
}
